import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { ErrorState } from '@/components/ui/ErrorState';
import { Button } from '@/components/ui/Button';
import apiClient from '@/lib/api-client';
import {
    ArrowLeft, Gift, Package, DollarSign, Tag,
    Layers, Sparkles, ShoppingBag, Percent,
    TrendingUp, Calculator, Clock, ChevronDown, ChevronUp,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';

export function HamperDetailPage() {
    const { hamperId } = useParams<{ hamperId: string }>();
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const [showItems, setShowItems] = useState(true);

    const { data: hamper, isLoading, isError, refetch } = useQuery({
        queryKey: ['hamper-detail', hamperId],
        queryFn: async () => {
            const { data } = await apiClient.get(`/tenants/${user!.tenantId}/hampers/${hamperId}`);
            return data?.data || data;
        },
        enabled: !!user?.tenantId && !!hamperId,
    });

    const formatNaira = (val: string) => `₦${parseFloat(val || '0').toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

    if (isLoading) return <div className="flex justify-center py-20"><Spinner size="xl" /></div>;
    if (isError || !hamper) return <ErrorState title="Hamper not found" onRetry={() => refetch()} />;

    const profit = parseFloat(hamper.sellingPrice || '0') - parseFloat(hamper.totalCost || '0');
    const margin = parseFloat(hamper.sellingPrice) > 0 ? (profit / parseFloat(hamper.sellingPrice)) * 100 : 0;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50/30">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-5xl mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button onClick={() => navigate('/hampers')} className="p-2 rounded-xl hover:bg-slate-100" type="button">
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div className={clsx('p-2.5 rounded-2xl shadow-lg', hamper.isClearance ? 'bg-gradient-to-br from-rose-500 to-pink-600' : 'bg-gradient-to-br from-amber-500 to-orange-600')}>
                                <Gift className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <h1 className="text-xl font-bold text-slate-900">{hamper.name}</h1>
                                    <Badge variant={hamper.isActive ? 'success' : 'danger'} size="sm">{hamper.isActive ? 'Active' : 'Inactive'}</Badge>
                                    {hamper.isClearance && <Badge variant="warning" size="sm">Clearance</Badge>}
                                </div>
                                <p className="text-sm text-slate-400">{hamper.slug}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-5xl mx-auto p-6 lg:p-8 space-y-6">
                {/* Hero Card */}
                <Card className="overflow-hidden">
                    <div className={clsx('h-2', hamper.isClearance ? 'bg-gradient-to-r from-rose-500 to-pink-600' : 'bg-gradient-to-r from-amber-500 to-orange-600')} />
                    <CardContent className="p-8">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {/* Visual Preview */}
                            <div className="flex items-center justify-center">
                                <div className={clsx(
                                    'w-48 h-48 rounded-3xl flex items-center justify-center shadow-2xl',
                                    hamper.isClearance ? 'bg-gradient-to-br from-rose-100 to-pink-200' : 'bg-gradient-to-br from-amber-100 to-orange-200'
                                )}>
                                    <Gift size={80} className={hamper.isClearance ? 'text-rose-500' : 'text-amber-500'} />
                                </div>
                            </div>

                            {/* Info */}
                            <div className="md:col-span-2 space-y-4">
                                {hamper.description && (
                                    <p className="text-slate-600 leading-relaxed">{hamper.description}</p>
                                )}

                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <PriceCard label="Selling Price" value={formatNaira(hamper.sellingPrice)} icon={DollarSign} color="text-emerald-600" bg="bg-emerald-50" />
                                    <PriceCard label="Total Cost" value={formatNaira(hamper.totalCost)} icon={Calculator} color="text-slate-600" bg="bg-slate-50" />
                                    <PriceCard label="Profit" value={formatNaira(String(profit))} icon={TrendingUp} color={profit >= 0 ? 'text-emerald-600' : 'text-rose-600'} bg={profit >= 0 ? 'bg-emerald-50' : 'bg-rose-50'} />
                                    <PriceCard label="Margin" value={`${margin.toFixed(0)}%`} icon={Percent} color="text-purple-600" bg="bg-purple-50" />
                                </div>

                                {hamper.serviceCharge > 0 && (
                                    <div className="flex items-center gap-2 text-sm text-slate-500">
                                        <Sparkles size={16} className="text-amber-500" />
                                        Service Charge: {formatNaira(hamper.serviceCharge)}
                                    </div>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Items Section */}
                <Card>
                    <button type="button" onClick={() => setShowItems(!showItems)}
                        className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-amber-100">
                                <Package size={18} className="text-amber-600" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-sm text-slate-800">Hamper Contents</h3>
                                <p className="text-xs text-slate-400">{hamper.items?.length || 0} items in this bundle</p>
                            </div>
                        </div>
                        {showItems ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
                    </button>
                    <AnimatePresence>
                        {showItems && (
                            <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                                <div className="border-t divide-y divide-slate-50">
                                    {hamper.items?.map((item: any, idx: number) => (
                                        <motion.div
                                            key={item.id}
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: idx * 0.05 }}
                                            className="flex items-center justify-between px-5 py-4 hover:bg-slate-50"
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                                                    <Package size={20} className="text-amber-600" />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-semibold text-slate-800">
                                                        {item.componentName || 'Component'}
                                                    </p>
                                                    <p className="text-xs text-slate-400">
                                                        {item.componentType?.replace(/_/g, ' ')} · Qty: {item.quantity}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-sm font-semibold text-slate-900">{formatNaira(item.unitCost)}</p>
                                                <p className="text-xs text-slate-400">Total: {formatNaira(item.totalCost)}</p>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>

                                {/* Totals */}
                                <div className="px-5 py-4 bg-slate-50 border-t space-y-1">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-slate-500">Items Total</span>
                                        <span className="font-semibold">{formatNaira(hamper.totalCost)}</span>
                                    </div>
                                    {hamper.serviceCharge > 0 && (
                                        <div className="flex justify-between text-sm">
                                            <span className="text-slate-500">Service Charge</span>
                                            <span>{formatNaira(hamper.serviceCharge)}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between text-base font-bold pt-2 border-t border-slate-200">
                                        <span>Selling Price</span>
                                        <span className="text-emerald-600">{formatNaira(hamper.sellingPrice)}</span>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </Card>

                {/* Metadata */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <MetaCard label="Created" value={new Date(hamper.createdAt).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })} icon={Clock} />
                    <MetaCard label="Updated" value={new Date(hamper.updatedAt).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })} icon={Clock} />
                    <MetaCard label="Status" value={hamper.isActive ? 'Active' : 'Inactive'} icon={hamper.isActive ? TrendingUp : Clock} />
                    <MetaCard label="Clearance" value={hamper.isClearance ? `Yes — ${formatNaira(hamper.clearancePrice || '0')}` : 'No'} icon={Tag} />
                </div>
            </div>
        </div>
    );
}

function PriceCard({ label, value, icon: Icon, color, bg }: { label: string; value: string; icon: React.ElementType; color: string; bg: string }) {
    return (
        <div className={`${bg} rounded-xl p-4 text-center`}>
            <Icon size={20} className={`${color} mx-auto mb-2`} />
            <p className="text-xs text-slate-400">{label}</p>
            <p className={`text-lg font-bold ${color} mt-0.5`}>{value}</p>
        </div>
    );
}

function MetaCard({ label, value, icon: Icon }: { label: string; value: string; icon: React.ElementType }) {
    return (
        <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 mb-1">
                <Icon size={14} className="text-slate-400" />
                <p className="text-xs text-slate-400 uppercase">{label}</p>
            </div>
            <p className="text-sm font-medium text-slate-800">{value}</p>
        </div>
    );
}